import Content from "./content";
import { buildSeoCard } from "~/review/seo-card";
import { loadClamp } from "~/es6/clamp-loader";
import FirmReviewEdit from "~/review/edit";
import FirmReviewItem from "~/review/item";
import FirmStarsReview from "~/review/stars-review";
import { updateReviewState, fetchActiveRequests, listReviewsComments, getDetail, IListReviewsCommentsOutput } from "~/util/providers/review";
import { PAGE_SUGGEST, PAGE_MY } from "~/mymaps2/reviews/constants";
import { CONFIG } from "~/config";
import { dateFormat } from "~/util/format";
import { getFirmLayout, clamp, reviewHasBadge, reviewHasCountBadge, reviewHasColoredBadge, reviewGetBadgeColor, getBadgeComposedComponent } from "~/util/review";
import { IReviewConf } from "~/interfaces";
import { IReview, IReviewItem } from "~/review/interfaces";
import { node, icon, isInViewport } from "~/util/dom";
import { store } from "~/state/store";
import { _ } from "~/i18n";
import { createEmojisPreview } from "~/ui/review/emojisPreview";
import { useScroll } from "~/util/usescroll";
import Notifications, { NOTIFICATIONS_TYPE } from "~/util/notifications";
import { addListener, removeListeners } from "~/lib/signals";
import ReviewPopup from "~/review/popupwc";

const LOADING_CLASS = "loading";
const HIDE_CLASS = "hide";
const DATA_EMPTY_CLASS = "data-empty";
const INACTIVE_CLASS = "inactive";
const FIRM_IS_IRELEVANT_CLASS = "firm-is-irelevant";

interface ICommentsOptions {
	data: {
		showWeather: boolean;
		description: null | Record<string, never>;
	};
}

// signal: firmreview-comments-myreview
export default class Comments extends Content<IReviewConf, ICommentsOptions> {
	_items: Array<FirmReviewItem>;

	_wasRequest: boolean;

	_firmReviewEdit: FirmReviewEdit;

	_logSource: string;

	_commentsData: null | IListReviewsCommentsOutput;

	_seoCard: HTMLScriptElement;

	_scrollToReviews: boolean;

	_scrollListener: ReturnType<typeof useScroll>;

	_sc: Array<string>;

	constructor(data: IReviewConf, options) {
		super(data, options);

		this._sc = [];
		this._items = [];
		this._wasRequest = false;
		this._firmReviewEdit = null;
		this._logSource = "recenze-poi";
		this._commentsData = null;
		this._seoCard = null;
		this._scrollToReviews = this._data.zoomToReviews;
		this._scrollListener = null;

		if (this._data.review) {
			window.addEventListener("review-report", this);
			window.addEventListener("firmreview-review-myreview", this);
			window.addEventListener("firmreview-comments-likeupdate", this);

			this._set();

			if (CONFIG.isScreenshoter) {
				if (this._data.recenze) {
					this._openReviewsList(true);
				} else {
					this._loadData();
				}
			} else if (this._scrollToReviews) {
				this._loadData();
			} else {
				setTimeout(() => {
					this._init();
				}, 100);
			}
		} else {
			this._dom.container.classList.add(DATA_EMPTY_CLASS);
		}

		this._sc.push(addListener(this, "reviewslist-activated", "_reviewsListActivated"));
		this._sc.push(addListener(this, "reviewslist-deactivated", "_reviewsListDeactivated"));
	}

	destroy() {
		while (this._items.length) {
			const item = this._items.pop();

			item.destroy();
		}

		this._seoCard?.remove();
		this._seoCard = null;

		removeListeners(this._sc);
		window.removeEventListener("review-report", this);
		window.removeEventListener("firmreview-review-myreview", this);
		window.removeEventListener("firmreview-comments-likeupdate", this);
		this._scrollListener && this._scrollListener();
		this._scrollListener = null;
		super.destroy();
	}

	tryToRunScroll() {
		if (!this._wasRequest) {
			this._onScroll();
		}
	}

	handleEvent(event: Event|CustomEvent) {
		switch (event.type) {
			case "review-report": {
				this._data.review.total--;

				this._set();
				this._loadData();
				break;
			}

			case "firmreview-review-myreview": {
				const customEvent = event as CustomEvent<{
					rusAdmin: boolean;
					review: IReview;
					myReview: IReviewItem;
				}>;
				const detail = customEvent.detail;
				const rusAdminChange = this._data.rusAdmin !== detail.rusAdmin;
				const myReviewChange = (!this._data.myReview && detail.myReview) || (this._data.myReview && !detail.myReview);

				this._data.rusAdmin = detail.rusAdmin;
				this._data.review = detail.review
					? {
						...detail.review,
						...this._data.review.alternatives ? { alternatives: this._data.review.alternatives } : {},
					}
					: this._data.review;
				this._data.myReview = detail.myReview;

				this._set();

				if (rusAdminChange) {
					this._rebuildComments();
				} else if (myReviewChange) {
					this._loadData();
				}
			}
				break;

			case "firmreview-comments-likeupdate": {
				const { detail } = event as CustomEvent;

				this._items.forEach(item => {
					item.updateLike(detail);
				});
				break;
			}

			default:
		}
	}

	_reviewsListActivated() {
		this._seoCard?.remove();
	}

	_reviewsListDeactivated() {
		if (this._seoCard) {
			document.head.appendChild(this._seoCard);
		}
	}

	_build() {
		this._dom.container.classList.add("content-comments");
		this._dom.container.classList.add("firm-content");
		this._dom.loader = node("p", { className: "loading" });

		const loaderGif = node("img", { src: "/img/loader.gif" });

		this._dom.loader.appendChild(loaderGif);

		const topSection = node("div", { className: "top-section" });
		const reviewsContent = node("div", { className: "reviews-content" });

		this._dom.container.appendChild(topSection);
		this._dom.container.appendChild(reviewsContent);
		// ulozime
		Object.assign(this._dom, {
			topSection, reviewsContent,
		});
	}

	// da se volat nkrat
	_set() {
		this._dom.topSection.innerHTML = "";
		this._dom.container.classList.remove(DATA_EMPTY_CLASS);
		this._dom.container.classList.remove(FIRM_IS_IRELEVANT_CLASS);
		this._dom.container.classList[this._data.inactive ? "add" : "remove"](INACTIVE_CLASS);
		this._dom.container.classList.remove("remove-top-border");

		if (this._options.data.showWeather !== false && !this._options.data.description) {
			this._dom.container.classList.add("remove-top-border");
		}

		const isFirmReview = this._data.isFirm;

		// nemame zadne hodnoceni -> nic nevytvorime
		if (!this._data.myReview && this._data.review.total <= 0) {
			this._dom.container.classList.add(DATA_EMPTY_CLASS);

			if (!Mapy.CUS.userweb && Mapy.CUS.userweb.firmDebug) {
				this._dom.container.classList.add(HIDE_CLASS);
			}

			this._createDebug();

			return;
		}

		this._dom.container.classList.remove(HIDE_CLASS);
		// uzivatel je vlastnik
		this._dom.container.classList[this._data.rusAdmin ? "add" : "remove"]("firm-owner");

		const infoArea = node("div", { className: "info-area" });

		if (!isFirmReview) {
			infoArea.classList.add("no-firm");
		}

		// hodnoceni
		if (reviewHasBadge(this._data.review)) {
			const percentageArea = node("div", { className: "percentage-area" });
			// info
			const reviewInfo = node("span", { className: "review-info" });
			const msg = [
				...reviewHasColoredBadge(this._data.review)
					? [_("detail.content.review2.bubbleInfo2")]
					: [],
				...reviewHasCountBadge(this._data.review)
					? [_("detail.content.review2.seznamReviewNotice")]
					: [],
			].join("<br /><br />");

			reviewInfo.appendChild(icon("square-info"));
			reviewInfo.appendChild(node("span", { className: "bubble", innerHTML: msg }));
			percentageArea.appendChild(reviewInfo);
			this._dom.topSection.appendChild(percentageArea);
			percentageArea.appendChild(node(getBadgeComposedComponent(), {
				type: reviewGetBadgeColor(this._data.review),
				size: "big",
				rating: this._data.review.review_rating_stars,
				text: this._data.review.review_rating_caption,
				reviews: this._data.review.total,
				lang: CONFIG.defaultLang,
			}));

			this._createDebug();
		} else {
			this._dom.container.classList.add(FIRM_IS_IRELEVANT_CLASS);

			this._createDebug();
		}

		if (!reviewHasBadge(this._data.review) && !this._data.inactive && !this._data.rusAdmin) {
			let caption = "";

			if (isFirmReview) {
				caption = this._data.myReview ? _("detail.content.review2.reviewTotalCount") : _("detail.content.review2.reviewComm");
			} else {
				caption = this._data.myReview ? _("detail.content.review2.reviewNoFirmTotalCount") : _("detail.content.review2.reviewNoFirmComm");
			}

			const infoText = node("p", { className: "desc", innerHTML: caption });

			infoArea.appendChild(infoText);
		}

		if (!this._data.myReview && !reviewHasBadge(this._data.review) && !this._data.inactive && !this._data.rusAdmin) {
			infoArea.append(
				node("span", { style: "display: block" }),
				this._getAddBtn(),
			);
		}

		// strukturko hodnoceni
		const emojiData = createEmojisPreview({
			config: this._data.structuredRating,
		});

		this._dom.topSection.append(emojiData.container);

		if (!emojiData.isHidden) {
			this._dom.topSection.classList.add("has-structured-review");
		}

		this._dom.topSection.appendChild(infoArea);

		// hodnoceni - hvezdoklikatko
		if (!this._data.myReview && reviewHasBadge(this._data.review) && !this._data.inactive && !this._data.rusAdmin) {
			if (isFirmReview) {
				const starsReview = new FirmStarsReview({
					data: this._data,
					logSource: this._logSource,
					logPosition: "bottom",
					setVisitTitle: true,
					onLoginError: data => {
						if (data.isOwner) {
							this._data.rusAdmin = true;
							this._dom.container.classList[this._data.review && this._data.rusAdmin ? "add" : "remove"]("firm-owner");
							starsReview.destroy();
							Notifications.notify({
								content: _("detail.content.review2.errorOwner"),
								type: NOTIFICATIONS_TYPE.ERROR,
							});
							this._rebuildComments();
							this._callEvent();
						} else if (data.myReview) {
							this._data.myReview = data.myReview;
							starsReview.destroy();
							this._set();
							this._createReviewPopup();
							this._callEvent();
						}
					},
					showPopupCb: putReviewData => {
						// ulozime
						if (putReviewData.review) {
							this._data.review = {
								...putReviewData.review,
							};
						}

						this._data.myReview = putReviewData.myReview;
						// popup
						this._createReviewPopup(putReviewData.myReview.rating, false);
					},
					onStructuredRating: data => {
						this._data.id = data.id;
						this._data.source = data.source;
						this._data.review = data.review;

						// popup
						this._createReviewPopup(data.rating, false);
					},
					onError: () => {
						this._log("reviews_fail_stars");
					},
				});

				if (this._data.openReviewPopup) {
					delete this._data.openReviewPopup;
				}

				this._dom.topSection.appendChild(starsReview.container);
			} else {
				const infoText = node("p", { className: "no-firm-review", innerHTML: _("detail.content.review2.reviewNoFirmComm") });

				infoText.append(
					node("span", { style: "display: block" }),
					this._getAddBtn(),
				);
				this._dom.topSection.appendChild(infoText);
			}
		}

		// vlastni hodnoceni
		if (this._data.myReview) {
			const thankYouCont = node("div", { className: "thank-you-cont" });

			this._firmReviewEdit = new FirmReviewEdit({
				data: {
					...this._data,
					myReview: {
						...this._data.myReview,
						actual: this._data.reviewMode !== "update",
					},
				},
				logSource: this._logSource,
				analytics: this._data.reviewMode === "update"
					? {
						source: "recenze-notification",
						type: "recenze-update",
						signal: `${this._data.typeId}_${this._data.id}`,
					}
					: {},
				isBottom: true,
				inactive: this._data.inactive,
				thankYouCont,
				update: this._data.reviewMode === "update",
				onSave: async putReviewData => {
					// ulozime
					if (putReviewData.review) {
						const alternatives = this._data.review.alternatives;

						this._data.review = {
							...putReviewData.review,
							...alternatives ? { alternatives } : {},
						};
					}

					this._data.myReview = putReviewData.myReview;
					// nastavime nova data
					this._set();
					this._callEvent();

					if (this._data.drrid) {
						updateReviewState(this._data.drrid, "rated_review");
					}

					// prejdeme do MM navrhu, pokud slo o aktualizaci a tedy sem jsme se dostali z URL/notifikace
					if (this._data.reviewMode === "update") {
						try {
							const response = await fetchActiveRequests();
							const result = await response.json();

							Mapy.getComponent("reviews").state = {
								activePage: result?.events?.length ? PAGE_SUGGEST : PAGE_MY,
							};
							Mapy.getComponent("mymaps").activate();
							Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("reviews"));
							// pokud to jakkoliv selze, jdeme do hodnoceni
						} catch (ex) {
							/* eslint-disable no-console */
							console.log(ex);
							Mapy.getComponent("reviews").state = {
								activePage: PAGE_MY,
							};
							Mapy.getComponent("mymaps").activate();
							Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("reviews"));
						}
					}
				},
				poi: {
					source: this._data.source,
					id: this._data.id,
					title: this._data.title,
					poitypeId: this._data.typeId,
					mark: this._data.mark,
				},
			});
			this._dom.topSection.appendChild(thankYouCont);
			this._dom.topSection.appendChild(this._firmReviewEdit.container);
			// odpoved na vlastni hodnoceni
			if (this._data.myReview.reply && this._data.myReview.reply.text) {
				this._buildReply();
			}
			// imprese
			this._myReviewImpress();

			// pokud chceme rovnou otevirat, tak otevreme - zpozdeni kvuli focusu
			setTimeout(() => {
				if (this._data.reviewMode === "update") {
					this._createReviewPopup();
				}
			}, 200);
		}
	}

	_buildReply() {
		const data = this._data.myReview.reply;
		const replyCont = node("div", { className: "myreview-reply-cont" });
		// vytvorime
		const replyInner = node("div", { className: "reply-inner" });
		const circle = node("span", { className: "circle" });

		circle.appendChild(icon("reply"));
		replyInner.appendChild(circle);

		const caption = node("h2", { className: "reply-title", textContent: _("detail.content.review2.reaction") });
		const replyDesc = node("p", { textContent: Mapy.Util.escapeHTML(data.text) });
		// datum
		const date = node("span", { className: `date`, textContent: dateFormat(new Date(data.updated), "d. mn yyyy") });

		replyInner.appendChild(caption);
		replyInner.appendChild(replyDesc);
		caption.appendChild(date);
		replyCont.appendChild(replyInner);
		this._dom.topSection.appendChild(replyCont);
		// orezeme
		loadClamp().then(() => {
			clamp(replyDesc, true);
		}, () => { /**/ });
	}

	_init() {
		// otevrit detail - neni potreba cekat na nacteni dalsich veci
		if (this._data.recenze) {
			this._openReviewsList(true);
		}

		if ((!this._wasRequest && this._isVisible()) || this._data.recenze) {
			this._loadData();
		}

		this._scrollListener = useScroll({
			onScroll: () => {
				this._onScroll();
			},
		});
	}

	_isVisible() {
		return isInViewport(this._dom.topSection);
	}

	_loadData() {
		// nemame vysledky z detailu? nema cenu nacitat
		if (this._data.review.total <= 0) {
			return;
		}

		// clean
		while (this._items.length) {
			const item = this._items.pop();

			item.destroy();
		}

		this._dom.reviewsContent.innerHTML = "";
		// nacteni
		this._wasRequest = true;
		this._dom.container.classList.add(LOADING_CLASS);
		this._dom.reviewsContent.appendChild(this._dom.loader);

		listReviewsComments({
			source: this._data.source,
			id: this._data.id,
			userId: this._data.myReview ? this._data.myReview.id : 0,
		}).then(data => {
			this._commentsData = data;

			if (data.myReview) {
				if (this._data.myReview) {
					this._data.myReview.weight = data.myReview.weight;
				} else {
					this._data.myReview = data.myReview;
				}

				if (this._firmReviewEdit) {
					this._firmReviewEdit.setMyReview(this._data.myReview);
				}
			}

			this._dom.loader.remove();
			this._dom.container.classList.remove(LOADING_CLASS);
			this._buildItemsFromData();

			if (this._scrollToReviews) {
				setTimeout(() => {
					this._dom.reviewsContent.scrollIntoView();
				}, 500);
				this._scrollToReviews = false;
			}
		}, () => {
			this._dom.loader.remove();
			this._dom.container.classList.remove(LOADING_CLASS);
			this._dom.reviewsContent.appendChild(node("p", { className: "empty-list", textContent: _("detail.content.review2.listReviewsError") }));
		});
	}

	_buildItemsFromData() {
		if (!this._commentsData) {
			return;
		}

		// vytvorime polozky
		const len = this._commentsData.reviews.length - 1;

		this._commentsData.reviews.forEach((comment, ind) => {
			const item = new FirmReviewItem({
				data: comment,
				index: ind + 1 + (this._data.myReview ? 1 : 0),
				// upravime podminku, aby si owner mohl editovat vlastni poi + aktivni detail
				isOwner: this._data.rusAdmin && !this._data.inactive,
				isFirm: this._data.isFirm,
				isTwin: this._data.isTwin,
				isDetailInactive: this._data.inactive,
				logSource: this._logSource,
				onOpen: () => {
					this._items.forEach(commentItem => commentItem.closeOpts());
				},
				backAction: () => {
					Mapy.getComponent("detail").setState({
						source: this._data.source,
						id: this._data.id,
					});
					Mapy.getComponent("userProfile").deactivate();
					Mapy.getComponent("history").save();
				},
				analytics: {
					sourcePoi: this._data.source,
					typeid: this._data.typeId,
					firmTwin: this._data.isTwin ? 1 : 0,
				},
				poi: {
					source: this._data.source,
					id: this._data.id,
					title: this._data.title,
					poitypeId: this._data.typeId,
					mark: this._data.mark,
				},
			});

			this._items.push(item);
			this._dom.reviewsContent.appendChild(item.container);

			if (comment.seoHidden) {
				item.container.style.display = "none";
			}

			if (ind === len && !this._commentsData.loadMore) {
				item.container.classList.add("no-bottom-border");
			}
		});

		loadClamp().then(() => {
			this._items.forEach(item => item.clamp());

			this._itemsImpress();
		}, () => { /**/ });

		if (this._commentsData.loadMore) {
			const more = node("div", { className: "more" });
			const moreLink = node("a", { href: `https://${CONFIG.BASE_URL}/?source=${this._data.source}&id=${this._data.id}&recenze=1`, textContent: `${_("detail.content.review2.showAll")} (${this._data.review.total})` });

			moreLink.setAttribute("data-log-ignore", "");
			moreLink.addEventListener("click", event => {
				event.preventDefault();
				event.stopPropagation();
				// analytika pro mapy
				this._log("click", {
					text: "Ukázat všechna hodnocení",
				});
				this._openReviewsList();
			});
			more.appendChild(moreLink);
			this._dom.reviewsContent.appendChild(more);
		}

		const detailData = store.getState().detail;

		// seo karta - nejsou otevreny vsechny recenze
		if (!this._seoCard && !this._data.recenze && this._data.review && detailData.data && detailData.data.extend) {
			this._seoCard = buildSeoCard(detailData, this._data.review, this._commentsData.reviews);
			document.head.appendChild(this._seoCard);
		}
	}

	_openReviewsList(load20 = false) {
		Mapy.getComponent("reviewslist").setState({
			id: this._data.id,
			load20,
			poiData: this._data,
			noAnalyticsForThree: !load20,
		}).activate();
		Mapy.getComponent("history").save();
	}

	_rebuildComments() {
		this._dom.reviewsContent.innerHTML = "";

		this._buildItemsFromData();
	}

	_createReviewPopup(forceRating = 0, ignoreAlternatives = false) {
		// vlozeni recenze
		const { alternatives, ...restOfReview } = this._data.review;

		/* eslint-disable no-new */
		new ReviewPopup({
			data: {
				...this._data,
				...this._data.review
					? {
						review: {
							...restOfReview,
							...ignoreAlternatives ? {} : { alternatives: [...Array.isArray(this._data.review.alternatives) ? this._data.review.alternatives : []] },
						},
					}
					: {},
			},
			textReview: !!this._data.myReview,
			logSource: this._logSource,
			logPosition: "bottom",
			fromOtherPOI: true,
			forceRating,
			onClose: closeData => {
				if (closeData.review) {
					const alternativesOut = this._data.review.alternatives;

					this._data.review = {
						...closeData.review,
						...alternativesOut ? { alternatives: alternativesOut } : {},
					};
				}

				this._data.myReview = closeData.myReview;
				// upravime se sami
				this._set();
				// znovu nacteme komentare
				this._loadData();
				this._callEvent();
			},
		});
	}

	_callEvent() {
		// zavolame callback pro review
		window.dispatchEvent(new CustomEvent("firmreview-comments-myreview", {
			detail: {
				rusAdmin: this._data.rusAdmin,
				review: this._data.review,
				myReview: this._data.myReview,
			},
		}));
		// data mezi taby
		const { data } = store.getState().detail;

		Mapy.getComponent("detail").getBroadCast()?.postMessage(JSON.stringify({
			type: "firm-review",
			data: {
				rusAdmin: this._data.rusAdmin,
				review: this._data.review,
				myReview: this._data.myReview,
				source: data?.source,
				id: data?.id,
			},
		}));
	}

	_createDebug() {
		// analyticky mod pro firemni hodnoceni
		if (Mapy.CUS.userweb && Mapy.CUS.userweb.firmDebug) {
			const firmDebug = node("div", { className: "firm-debug", style: "margin: 25px 0;" });

			firmDebug.appendChild(node("p", { innerHTML: `Vážený počet recenzí: <strong>${this._data.review.review_weight.toFixed(3)}</strong>`, style: "font-style: italic; color: gray;" }));
			firmDebug.appendChild(Mapy.Util.node("p", {
				innerHTML: `use rating: <strong>${(this._data.review.use_rating || 0).toString()}</strong>, `
					+ `use total: <strong>${this._data.review.use_total ? (this._data.review.use_total || 0).toString() : "neexistuje"}</strong>`,
				style: "font-style: italic; color: gray;",
			}));

			if (!(this._data.review && reviewHasColoredBadge(this._data.review))) {
				firmDebug.appendChild(node("p", { innerHTML: `Prům. hodnocení: <strong>${(Math.max(this._data.review.review_rating, 0) / 20).toFixed(2)}</strong>`, style: "font-style: italic; color: gray;" }));
				firmDebug.appendChild(node("p", { innerHTML: `Počet recenzí: <strong>${this._data.review.total}</strong>`, style: "font-style: italic; color: gray;" }));
			}

			this._dom.topSection.appendChild(firmDebug);
		}
	}

	_myReviewImpress() {
		if (this._firmReviewEdit && isInViewport(this._firmReviewEdit.header)) {
			this._firmReviewEdit.firmImpressAnalytics();
		}
	}

	_itemsImpress() {
		this._items.forEach(item => {
			if (isInViewport(item.header)) {
				item.firmImpressAnalytics();
			}
		});
	}

	_getAddBtn() {
		// tlacitko pridat hodnoceni
		const btnAddReview = node("button");

		btnAddReview.classList.add("addreview-btn");
		btnAddReview.setAttribute("data-log-ignore", "true");
		btnAddReview.addEventListener("click", () => {
			// analytika
			this._log("click", {
				text: "Přidat hodnocení",
			});

			const { user } = store.getState().user;

			(user.state === "green"
				? Promise.resolve()
				: Mapy.getComponent("login").open({
					analyticsData: {
						loginOrigin: "reviewsAdd",
						section: "reviews",
					},
					motivation: "review",
				})
			).then(() => {
				// pridani hodnoceni
				(user.state === "green" ? Promise.resolve(this._data) : getDetail({ source: this._data.source, id: this._data.id })).then(data => {
					if (data.isOwner) {
						this._data.rusAdmin = true;
						this._dom.container.classList[this._data.rusAdmin ? "add" : "remove"]("firm-owner");
						Notifications.notify({
							content: _("detail.content.review2.errorOwner"),
							type: NOTIFICATIONS_TYPE.ERROR,
						});
						this._rebuildComments();
						this._callEvent();

						return;
					} else if (data.myReview && Object.keys(data.myReview).length) {
						this._data.myReview = data.myReview;
						this._set();
						this._createReviewPopup();
						this._callEvent();

						return;
					}

					// popup
					this._createReviewPopup();
				}, () => { /**/ });
			});
		});
		btnAddReview.appendChild(icon("star"));
		btnAddReview.appendChild(node("span", { textContent: _("detail.content.review2.addReview") }));

		return btnAddReview;
	}

	_log(name: string, data?: object) {
		// analytika
		/* eslint-disable camelcase */
		Mapy.getComponent("logger").log(name, {
			position: "bottom",
			source: this._logSource,
			section: "reviews",
			rating_layout: getFirmLayout(this._data.review, this._data.booking, this._data.myReview, this._data.isFirm),
			type: "recenze",
			sourcePoi: this._data.source,
			typeid: this._data.typeId,
			firmTwin: this._data.isTwin ? 1 : 0,
			...data,
		});
	}

	_onScroll() {
		if (!this._wasRequest && this._isVisible()) {
			this._loadData();
		}

		this._myReviewImpress();
		this._itemsImpress();
	}
}
